import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { AdSlot } from "@/components/AdSlot";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FeaturedSvg, type IconKey } from "@/components/seo/FeaturedSvg";
import {
  todayISO,
  parseISO,
  diffDays,
  formatLong,
  calendarDatePath,
  DAY_NAMES_ID,
  dayOfWeek,
} from "@/lib/calendar/dates";
import { gregorianToJawa } from "@/lib/calendar/jawa";
import { Share2, Copy, PartyPopper, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { TERM_ENTITIES } from "@/lib/seo/entities";

export interface SinceCalculatorProps {
  /** Used for h1 + meta */
  title: string;
  /** Short paragraph under hero */
  intro: string;
  /** Label for the date input */
  dateLabel: string;
  /** Word used in result, e.g. "jadian", "menikah", "putus", "lahir". */
  verb: string;
  /** Frasa untuk congrats heading (default: verb). Misal "menjalani LDR". */
  congratsVerb?: string;
  /** Featured svg theme */
  iconKey: IconKey;
  /** Slug used to read/write the URL ?d= search param. */
  storageKey: string;
  /** FAQ items unique to this tool */
  faq: { q: string; a: string }[];
  /** PSEO body content (rendered as paragraphs) */
  body: { heading: string; text: string }[];
  /** Path of this tool, used for breadcrumbs/share. */
  path: string;
}

export function SinceCalculator(props: SinceCalculatorProps) {
  const [date, setDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [nameA, setNameA] = useState<string>("");
  const [nameB, setNameB] = useState<string>("");
  const [celebrate, setCelebrate] = useState<boolean>(false);

  // Hydrate from URL ?d= or localStorage on mount.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const q = url.searchParams.get("d");
    const qTo = url.searchParams.get("to");
    const qa = url.searchParams.get("a");
    const qb = url.searchParams.get("b");
    if (q && parseISO(q)) {
      setDate(q);
    } else {
      const saved = window.localStorage.getItem(`since-${props.storageKey}`);
      if (saved && parseISO(saved)) setDate(saved);
    }
    if (qTo && parseISO(qTo)) setEndDate(qTo);
    if (qa) setNameA(qa);
    if (qb) setNameB(qb);
    if (!qa) {
      const sa = window.localStorage.getItem(`since-${props.storageKey}-a`);
      if (sa) setNameA(sa);
    }
    if (!qb) {
      const sb = window.localStorage.getItem(`since-${props.storageKey}-b`);
      if (sb) setNameB(sb);
    }
  }, [props.storageKey]);

  useEffect(() => {
    if (typeof window === "undefined" || !date) return;
    window.localStorage.setItem(`since-${props.storageKey}`, date);
  }, [date, props.storageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (nameA) window.localStorage.setItem(`since-${props.storageKey}-a`, nameA);
    if (nameB) window.localStorage.setItem(`since-${props.storageKey}-b`, nameB);
  }, [nameA, nameB, props.storageKey]);

  const result = useMemo(() => {
    const start = parseISO(date);
    const refISO = endDate && parseISO(endDate) ? endDate : todayISO();
    const ref = parseISO(refISO)!;
    if (!start) return null;
    const totalDays = diffDays(date, refISO);
    let years = ref.y - start.y;
    let months = ref.m - start.m;
    let days = ref.d - start.d;
    if (days < 0) {
      months -= 1;
      const prev = new Date(Date.UTC(ref.y, ref.m - 1, 0));
      days += prev.getUTCDate();
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }
    const dow = dayOfWeek(start.y, start.m, start.d);
    const jawa = gregorianToJawa(start.y, start.m, start.d);
    const endJawa = gregorianToJawa(ref.y, ref.m, ref.d);
    const endDow = dayOfWeek(ref.y, ref.m, ref.d);
    return {
      start,
      end: ref,
      endIsToday: refISO === todayISO(),
      totalDays,
      years,
      months,
      days,
      weeks: Math.floor(totalDays / 7),
      hours: totalDays * 24,
      minutes: totalDays * 24 * 60,
      dayName: DAY_NAMES_ID[dow],
      weton: jawa.weton,
      endDayName: DAY_NAMES_ID[endDow],
      endWeton: endJawa.weton,
    };
  }, [date, endDate]);

  function compute() {
    if (!date) {
      toast.error("Pilih tanggal mulai dulu");
      return;
    }
    setCelebrate(true);
    setTimeout(() => setCelebrate(false), 2200);
  }

  function share() {
    if (typeof window === "undefined" || !date) return;
    const url = new URL(window.location.href);
    url.searchParams.set("d", date);
    if (endDate) url.searchParams.set("to", endDate); else url.searchParams.delete("to");
    if (nameA) url.searchParams.set("a", nameA); else url.searchParams.delete("a");
    if (nameB) url.searchParams.set("b", nameB); else url.searchParams.delete("b");
    navigator.clipboard.writeText(url.toString()).then(() => {
      toast.success("Link hasil disalin");
    });
  }

  const congrats = (() => {
    if (!result) return "";
    const verb = props.congratsVerb ?? props.verb;
    const breakdown = `${result.years} tahun, ${result.months} bulan, ${result.days} hari`;
    if (nameA && nameB) {
      return `Selamat ${nameA} dan ${nameB}, kalian sudah ${verb} selama ${breakdown}.`;
    }
    if (nameA) return `Selamat ${nameA}, kamu sudah ${verb} selama ${breakdown}.`;
    return `Kamu sudah ${verb} selama ${breakdown}.`;
  })();

  // Pull defined-term entities related to this tool (Weton/Neptu/Pasaran).
  const termEntities = [TERM_ENTITIES.weton, TERM_ENTITIES.neptu, TERM_ENTITIES.pasaran];

  const ld = [
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: props.title,
      applicationCategory: "UtilityApplication",
      operatingSystem: "Any",
      description: props.intro,
      inLanguage: "id-ID",
      offers: { "@type": "Offer", price: "0", priceCurrency: "IDR" },
      about: termEntities,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Beranda", item: "https://hari.co.id/" },
        { "@type": "ListItem", position: 2, name: "Tools", item: "https://hari.co.id/tools/" },
        { "@type": "ListItem", position: 3, name: props.title, item: `https://hari.co.id${props.path.startsWith("/") ? props.path : "/" + props.path}` },
      ],
    },
  ];

  return (
    <SiteShell>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
      <style>{`
        @keyframes confetti-fall { 0%{transform:translateY(-20px) rotate(0);opacity:0} 10%{opacity:1} 100%{transform:translateY(360px) rotate(540deg);opacity:0} }
        .confetti-piece { position:absolute; top:0; width:8px; height:14px; border-radius:2px; animation: confetti-fall 2.2s linear forwards; }
      `}</style>
      <div className="py-6 md:py-10">
        <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-1">
            <li><Link to={"/" as never} className="hover:text-foreground">Beranda</Link></li>
            <li>/</li>
            <li><Link to={"/tools" as never} className="hover:text-foreground">Kalkulator</Link></li>
            <li>/</li>
            <li className="text-foreground">{props.title}</li>
          </ol>
        </nav>

        <header className="rounded-2xl border border-border bg-card p-5 md:p-7">
          <div className="flex flex-col items-start gap-5 md:flex-row md:items-center">
            <FeaturedSvg
              variant="tool"
              title={props.title}
              subtitle={props.intro.slice(0, 80)}
              iconKey={props.iconKey}
              className="w-full max-w-xs md:w-72 md:flex-shrink-0"
            />
            <div className="flex-1">
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">{props.title}</h1>
              <p className="mt-2 text-sm text-muted-foreground md:text-base">{props.intro}</p>
            </div>
          </div>
        </header>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-card p-4 sm:p-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="d">{props.dateLabel}</Label>
                  <Input
                    id="d"
                    type="date"
                    max={todayISO()}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="dto">Tanggal sampai (opsional)</Label>
                  <Input
                    id="dto"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="mt-1"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">Kosongkan untuk menghitung sampai hari ini.</p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="na">Nama kamu (opsional)</Label>
                  <Input
                    id="na"
                    placeholder="Contoh: Doni"
                    value={nameA}
                    onChange={(e) => setNameA(e.target.value)}
                    className="mt-1"
                    maxLength={32}
                  />
                </div>
                <div>
                  <Label htmlFor="nb">Nama dia (opsional)</Label>
                  <Input
                    id="nb"
                    placeholder="Contoh: Dina"
                    value={nameB}
                    onChange={(e) => setNameB(e.target.value)}
                    className="mt-1"
                    maxLength={32}
                  />
                </div>
              </div>
              <Button onClick={compute} className="w-full sm:w-auto">
                <PartyPopper className="mr-2 h-4 w-4" /> Hitung
              </Button>
            </div>

            {result && result.totalDays >= 0 && (
              <div className="relative overflow-hidden rounded-lg border border-border bg-card p-4 sm:p-6">
                {celebrate && (
                  <div aria-hidden="true" className="pointer-events-none absolute inset-0">
                    {Array.from({ length: 30 }).map((_, i) => (
                      <span
                        key={i}
                        className="confetti-piece"
                        style={{
                          left: `${(i * 97) % 100}%`,
                          background: ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#a855f7"][i % 5],
                          animationDelay: `${(i % 10) * 0.08}s`,
                        }}
                      />
                    ))}
                  </div>
                )}
                <p className="text-base font-semibold text-primary md:text-lg">
                  {congrats}
                </p>
                <p className="text-sm text-muted-foreground">
                  Sejak {formatLong(result.start.y, result.start.m, result.start.d)} ({result.dayName} {result.weton})
                  {!result.endIsToday && (
                    <>
                      {" "}sampai {formatLong(result.end.y, result.end.m, result.end.d)} ({result.endDayName} {result.endWeton})
                    </>
                  )}
                </p>
                <p className="mt-2 text-3xl font-bold">
                  {result.totalDays.toLocaleString("id-ID")} hari
                </p>
                <p className="text-base text-muted-foreground">
                  Setara dengan{" "}
                  <strong>
                    {result.years} tahun, {result.months} bulan, {result.days} hari
                  </strong>.
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                  <Stat label="Total hari" value={result.totalDays.toLocaleString("id-ID")} />
                  <Stat label="Total minggu" value={result.weeks.toLocaleString("id-ID")} />
                  <Stat label="Total jam" value={result.hours.toLocaleString("id-ID")} />
                  <Stat label="Total menit" value={result.minutes.toLocaleString("id-ID")} />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={share}>
                    <Share2 className="mr-2 h-3.5 w-3.5" /> Bagikan hasil
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={(() => {
                        if (typeof window === "undefined") return "#";
                        const u = new URL(window.location.href);
                        if (date) u.searchParams.set("d", date);
                        if (endDate) u.searchParams.set("to", endDate);
                        if (nameA) u.searchParams.set("a", nameA);
                        if (nameB) u.searchParams.set("b", nameB);
                        const text = `${congrats} ${u.toString()}`;
                        return `https://wa.me/?text=${encodeURIComponent(text)}`;
                      })()}
                    >
                      <MessageCircle className="mr-2 h-3.5 w-3.5" /> WhatsApp
                    </a>
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to={calendarDatePath(result.start.y, result.start.m, result.start.d) as never}>
                      Detail tanggal mulai
                    </Link>
                  </Button>
                </div>
              </div>
            )}
            {result && result.totalDays < 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
                Tanggal yang kamu pilih ada di masa depan. Kalkulator ini menghitung hari yang sudah berlalu.
              </div>
            )}

            {/* PSEO body */}
            <article className="prose prose-sm prose-neutral max-w-none dark:prose-invert">
              {props.body.map((b, i) => (
                <section key={i}>
                  <h2>{b.heading}</h2>
                  <p>{b.text}</p>
                </section>
              ))}
            </article>

            {/* FAQ */}
            <section className="rounded-xl border border-border bg-card p-5 md:p-6">
              <h2 className="text-lg font-semibold">Tanya Jawab</h2>
              <div className="mt-4 space-y-4 text-sm">
                {props.faq.map((f, i) => (
                  <div key={i}>
                    <p className="font-semibold">{f.q}</p>
                    <p className="mt-1 text-muted-foreground">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
          <aside className="space-y-4">
            <AdSlot size="rectangle" position="tools-result" />
            <div className="rounded-lg border border-border bg-card p-4 text-sm">
              <p className="font-semibold">Kalkulator lain</p>
              <ul className="mt-2 space-y-1.5 text-muted-foreground">
                <li><Link to={"/tools/hari-jadian" as never} className="hover:text-primary">Hari Jadian</Link></li>
                <li><Link to={"/tools/hari-lahir" as never} className="hover:text-primary">Hari Lahir</Link></li>
                <li><Link to={"/tools/hari-menikah" as never} className="hover:text-primary">Hari Menikah</Link></li>
                <li><Link to={"/tools/hari-putus" as never} className="hover:text-primary">Hari Putus / Gamon</Link></li>
                <li><Link to={"/tools/hari-sahabat" as never} className="hover:text-primary">Lama Bersahabat</Link></li>
                <li><Link to={"/tools/hari-ldr" as never} className="hover:text-primary">Lama LDR</Link></li>
                <li><Link to={"/tools/cek-weton" as never} className="hover:text-primary">Cek Weton</Link></li>
                <li><Link to={"/tools/kalkulator-hari" as never} className="hover:text-primary">Kalkulator Hari</Link></li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </SiteShell>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}

// expose Copy icon used implicitly via lucide
void Copy;